// @ts-check

/**
 * Cart → Hydrogen discount-check sync.
 *
 * Listens for every successful cart mutation on the storefront and POSTs the
 * current cart state + customer MF08 (loyalty golden-record-id) to the
 * Hydrogen App Proxy. The Hydrogen route logs receipt and is the entry point
 * for the future onward call to the discount engine — this client side just
 * delivers the payload with a unix `sentAt` so latency can be measured server-
 * side.
 *
 * Why a separate module:
 *   - Cart events are dispatched from several components (`product-form.js`,
 *     `component-cart-items.js`, `cart-discount.js`, `store-selector-drawer.js`).
 *     Centralising the Hydrogen POST here keeps those components decoupled.
 *   - We need a single debounced point so that a sequence like "SSD clears
 *     cart then re-renders" produces one POST, not two.
 *
 * Listened events (both bubble to `document`):
 *   - `cart:update`     — adds (CartAddEvent reuses this name), qty changes,
 *                         removals, silent SSD clears.
 *   - `discount:update` — apply/remove discount code (totals changed).
 *
 * Skipped:
 *   - Failed adds where `event.detail.data.didError === true` (cart is
 *     unchanged from the storefront's perspective).
 *   - When `data-customer-mf08` is empty (guest or unlinked customer) — there
 *     is no loyalty discount surface to evaluate yet, so don't burn requests.
 *     Once the discount engine handles anonymous carts we can drop the gate.
 *
 * Endpoint:
 *   POST /apps/api/cart/discount-check?brand={baker|cobsca|cobsus}
 *   Body: { cart: <full /cart.js shape>, customerMf08: <string|null>,
 *           sentAt: <unix-seconds> }
 */

const ENDPOINT_BASE = '/apps/api/cart/discount-check';
const DEBOUNCE_MS = 250;

let debounceTimer = /** @type {ReturnType<typeof setTimeout> | null} */ (null);
let inFlightAbort = /** @type {AbortController | null} */ (null);
let dispatchGeneration = 0;
let initialized = false;

function readRootData() {
  const root = document.documentElement;
  const mf08 = (root.dataset.customerMf08 || '').trim();
  const brand = (root.dataset.hydrogenApiBrand || '').trim();
  return { mf08, brand };
}

async function fetchCartSnapshot(signal) {
  try {
    const response = await fetch('/cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
      signal,
    });
    if (!response.ok) return null;
    const cart = await response.json();
    return cart && typeof cart === 'object' ? cart : null;
  } catch {
    return null;
  }
}

async function dispatchDiscountCheck(triggerSource) {
  const { mf08, brand } = readRootData();

  // Skip guests / unlinked customers — see header doc.
  if (!mf08) return;

  if (inFlightAbort) {
    inFlightAbort.abort();
  }
  const abortController = new AbortController();
  inFlightAbort = abortController;
  const generation = ++dispatchGeneration;

  const cart = await fetchCartSnapshot(abortController.signal);
  if (!cart || generation !== dispatchGeneration) return;

  const url = brand
    ? `${ENDPOINT_BASE}?brand=${encodeURIComponent(brand)}`
    : ENDPOINT_BASE;

  const sentAt = Math.floor(Date.now() / 1000);

  try {
    await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        cart,
        customerMf08: mf08,
        sentAt,
        trigger: triggerSource || null,
      }),
      signal: abortController.signal,
    });
  } catch (error) {
    // AbortError means a newer event superseded this one — not a real failure.
    if (error && /** @type {{name?: string}} */ (error).name === 'AbortError') {
      return;
    }
    // Best-effort: this is a side-channel for the discount engine, the cart
    // mutation has already succeeded. Surface in the console for ops without
    // breaking the shopper flow.
    // eslint-disable-next-line no-console
    console.warn('[cart-hydrogen-sync] discount-check POST failed', error);
  } finally {
    if (inFlightAbort === abortController) {
      inFlightAbort = null;
    }
  }
}

function scheduleDispatch(triggerSource) {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    debounceTimer = null;
    void dispatchDiscountCheck(triggerSource);
  }, DEBOUNCE_MS);
}

function handleCartUpdate(event) {
  const detail = /** @type {{detail?: {data?: {didError?: boolean, source?: string}}}} */ (event)?.detail;
  if (detail?.data?.didError === true) return;
  scheduleDispatch(detail?.data?.source || event.type);
}

function init() {
  if (initialized) return;
  initialized = true;

  document.addEventListener('cart:update', handleCartUpdate);
  document.addEventListener('discount:update', handleCartUpdate);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
